// Pieces every receiver journey screen shares: the page frame, a status badge, how a failed call
// reads (with the code and trace id to quote to support), the page buttons, the holder's document
// fields, and whether the session's role may act on the receiver. The screens' stylesheet is
// injected once, with this module.
import receptoraStyles from './receptora.css?inline';
import { Option } from 'effect';
import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { Button, StatusBadge } from '@finnest/ui';
import type { StatusTone } from '@finnest/ui';
import type {
  OpinReceiverConnectionStatus,
  OpinReceiverInsuranceType,
} from '@finnest/power-admin-contracts/opin-receiver';
import { usePermission } from '../../permissions/session-permissions.ts';
import type { PermissionState } from '../../permissions/session-permissions.ts';
import { FailureReference } from '../../ui/failure/failure-reference.tsx';
import type { ReceiverCallFailure } from '../client/calls.ts';
import {
  STATUS_LABELS,
  TYPE_LABELS,
  avatarColorOf,
  failureRefusal,
  initialsOf,
  failureText,
  isReceiverDisabled,
  punctuateCnpj,
  punctuateCpf,
  typedCnpj,
  typedCpf,
} from './format.ts';

const receptoraStyle = document.createElement('style');
receptoraStyle.textContent = receptoraStyles;
document.head.append(receptoraStyle);

const STATUS_TONES: Readonly<Record<OpinReceiverConnectionStatus, StatusTone>> = {
  pending: 'neutral',
  awaiting_auth: 'warning',
  syncing: 'warning',
  active: 'success',
  error: 'danger',
  revoked: 'neutral',
  revocation_pending: 'warning',
  expired: 'neutral',
};

// An active connection whose last sync stored some product families and not others.
const PARTIAL_LABEL = 'Ativa · sincronização parcial';

/** A connection's status; an active one whose last sync was partial says so, as a warning. */
export const ConnectionStatusBadge = ({
  status,
  partial,
}: {
  readonly status: OpinReceiverConnectionStatus;
  readonly partial: boolean;
}): JSX.Element =>
  partial && status === 'active' ? (
    <StatusBadge tone="warning">{PARTIAL_LABEL}</StatusBadge>
  ) : (
    <StatusBadge tone={STATUS_TONES[status]}>{STATUS_LABELS[status]}</StatusBadge>
  );

export const ReceiverPage = ({
  title,
  lead,
  actions,
  children,
}: {
  readonly title: string;
  readonly lead: string;
  readonly actions: ReactNode;
  readonly children: ReactNode;
}): JSX.Element => (
  <section className="admin-page receptora-page">
    <header className="admin-page-header">
      <div>
        <span className="admin-eyebrow">Open Insurance · Receptora</span>
        <h1>{title}</h1>
        <p>{lead}</p>
      </div>
      {actions}
    </header>
    {children}
  </section>
);

/**
 * A failed call's alert: what could not be done and why, with the error code and trace id to quote
 * to support when Power refused it naming a code.
 */
export const FailedCallAlert = ({
  lead,
  failure,
}: {
  readonly lead: string;
  readonly failure: ReceiverCallFailure;
}): JSX.Element => {
  const refusal = failureRefusal(failure);
  return (
    <div role="alert">
      <p>
        {lead}: {failureText(failure)}.
      </p>
      {Option.isSome(refusal) && <FailureReference refusal={refusal.value} />}
    </div>
  );
};

/** A failed call: the receiver being disabled reads as such, anything else as its reason. */
export const CallFailure = ({ failure }: { readonly failure: ReceiverCallFailure }): JSX.Element =>
  isReceiverDisabled(failure) ? (
    <p role="alert">Receptora desabilitada nesta implantação.</p>
  ) : (
    <FailedCallAlert lead="Não foi possível concluir" failure={failure} />
  );

/** The page a list is on, with the way to the pages around it. */
export const Pager = ({
  page,
  totalPages,
  onPage,
}: {
  readonly page: number;
  readonly totalPages: number;
  readonly onPage: (page: number) => void;
}): JSX.Element => (
  <nav aria-label="Páginas" hidden={totalPages <= 1}>
    <Button
      disabled={page <= 1}
      onClick={() => {
        onPage(page - 1);
      }}
    >
      Anterior
    </Button>
    <span>
      Página {page} de {totalPages}
    </span>
    <Button
      disabled={page >= totalPages}
      onClick={() => {
        onPage(page + 1);
      }}
    >
      Próxima
    </Button>
  </nav>
);

/** A holder's CPF or CNPJ field: punctuated as typed, its value only the document's characters. */
export const DocumentField = ({
  label,
  kind,
  value,
  onChange,
}: {
  readonly label: string;
  readonly kind: 'cpf' | 'cnpj';
  readonly value: string;
  readonly onChange: (value: string) => void;
}): JSX.Element => (
  <label>
    {label}
    <input
      autoComplete="off"
      inputMode={kind === 'cpf' ? 'numeric' : 'text'}
      value={kind === 'cpf' ? punctuateCpf(value) : punctuateCnpj(value)}
      onChange={(event) => {
        onChange(kind === 'cpf' ? typedCpf(event.target.value) : typedCnpj(event.target.value));
      }}
    />
  </label>
);

// The roles Power's `acting` admits to create, sync or revoke a connection; an auditor only reads.
const ACTING = ['unread', 'unreadable', 'reads', 'acts'] as const;

/** The session's standing: role not read yet, unreadable, a reading role, or one that acts. */
export type Acting = (typeof ACTING)[number];

const ACTING_OF = {
  unread: 'unread',
  unreadable: 'unreadable',
  denied: 'reads',
  allowed: 'acts',
} as const satisfies Record<PermissionState, Acting>;

// Acting on the receiver needs `common.write`. Power refuses an action to a role it does not admit
// either way; the screens offer it only to one it admits.
export const useActing = (): Acting => ACTING_OF[usePermission('common.write')];

/** A transmitter's avatar: its initials on a colour its id keeps stable. */
export const Avatar = ({
  name,
  colorKey,
  size = 'regular',
}: {
  readonly name: string;
  readonly colorKey: string;
  readonly size?: 'regular' | 'large';
}): JSX.Element => (
  <span
    className="receptora-avatar"
    data-size={size}
    aria-hidden="true"
    style={{ background: avatarColorOf(colorKey) }}
  >
    {initialsOf(name)}
  </span>
);

/** The consented data types as pills: the first `max`, then how many more, named on hover. */
export const TypePills = ({
  types,
  max,
}: {
  readonly types: readonly OpinReceiverInsuranceType[];
  readonly max: number;
}): JSX.Element => {
  const shown = types.slice(0, max);
  const rest = types.slice(max);
  return (
    <span className="receptora-pills">
      {shown.map((type) => (
        <span key={type} className="receptora-pill">
          {TYPE_LABELS[type]}
        </span>
      ))}
      {rest.length > 0 && (
        <span
          className="receptora-pill"
          data-more=""
          title={rest.map((type) => TYPE_LABELS[type]).join(', ')}
        >
          +{rest.length}
        </span>
      )}
    </span>
  );
};

const COPIED_MS = 2000;

/** A value the operator copies with one click: shown as `label`, saying so once copied. */
export const CopyButton = ({
  value,
  label,
  title,
}: {
  readonly value: string;
  readonly label: string;
  readonly title: string;
}): JSX.Element => {
  const [copied, setCopied] = useState(false);
  // Once copied, the chip says so for a while; not copied, it already reads its label.
  useEffect(() => {
    const timer = setTimeout(
      () => {
        setCopied(false);
      },
      copied ? COPIED_MS : 0,
    );
    return (): void => {
      clearTimeout(timer);
    };
  }, [copied]);
  const copy = async (): Promise<void> => {
    await navigator.clipboard.writeText(value);
    setCopied(true);
  };
  return (
    <button
      type="button"
      className="receptora-copy"
      title={title}
      onClick={() => {
        void copy();
      }}
    >
      {copied ? 'Copiado' : label}
    </button>
  );
};

const MINUTE_MS = 60_000;

/** The current time, moved once a minute: relative times ("há 5 min") stay right on screen. */
export const useNow = (): number => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => {
      setNow(Date.now());
    }, MINUTE_MS);
    return (): void => {
      clearInterval(timer);
    };
  }, []);
  return now;
};
